// ---------------------------------------------------------------------------
// Test Mode — settings, integrations, services, categories (demo audit).
// ---------------------------------------------------------------------------

import { isoDateTime } from './company';
import { buildPeople } from './people';
import { buildTasks } from './tasks';
import { buildInvoices } from './invoices';
import { buildSubscriptions } from './subscriptions';
import { PROJECT_SEEDS } from './projects';
import { CLIENT_SEEDS } from './clients';
import { SUPPLIER_SEEDS } from './suppliers';
import type { SettingsIntegration, IntegrationField } from '../lib/settingsApi';
import type { AiUsagePeriod, AiUsageSummary } from '../lib/aiUsageApi';
import type { BankDefaultsResponse } from '../lib/settingsApi';

// Keep old Integration export for backwards compat (if anything imported it).
export type Integration = SettingsIntegration;

function integrationFields(key: string): IntegrationField[] {
  switch (key) {
    case 'smartbill':
      return [
        { key: 'username', label: 'Utilizator', type: 'text', secret: false, required: true },
        { key: 'token', label: 'Token API', type: 'password', secret: true, required: true },
        {
          key: 'companyVatCode',
          label: 'CUI Companie',
          type: 'text',
          secret: false,
          required: true,
        },
      ];
    case 'googleCalendar':
      return [
        { key: 'clientId', label: 'Client ID', type: 'text', secret: false, required: true },
        {
          key: 'clientSecret',
          label: 'Client Secret',
          type: 'password',
          secret: true,
          required: true,
        },
      ];
    case 'anthropic':
      return [{ key: 'apiKey', label: 'API Key', type: 'password', secret: true, required: true }];
    case 'smtp':
      return [
        { key: 'host', label: 'Host', type: 'text', secret: false, required: true },
        { key: 'port', label: 'Port', type: 'number', secret: false, required: true },
        { key: 'user', label: 'Utilizator', type: 'text', secret: false, required: true },
        { key: 'pass', label: 'Parolă', type: 'password', secret: true, required: true },
        { key: 'from', label: 'Adresă expeditor', type: 'text', secret: false, required: true },
      ];
    case 'googleDrive':
      return [
        { key: 'clientId', label: 'Client ID', type: 'text', secret: false, required: true },
        {
          key: 'clientSecret',
          label: 'Client Secret',
          type: 'password',
          secret: true,
          required: true,
        },
      ];
    case 'anaf':
      return [
        {
          key: 'certificate',
          label: 'Certificat digital',
          type: 'textarea',
          secret: true,
          required: true,
        },
        {
          key: 'password',
          label: 'Parolă certificat',
          type: 'password',
          secret: true,
          required: true,
        },
      ];
    default:
      return [];
  }
}

// Keys and scopes mirror backend/src/lib/integrations/registry.ts — the card
// looks up its help text and its API path by key, so a fixture key that isn't a
// real integration key silently breaks both.
export function buildSettingsIntegrations(): SettingsIntegration[] {
  return [
    {
      key: 'smartbill',
      label: 'SmartBill',
      description: 'Sincronizare facturi și raportare financiară.',
      scope: 'company',
      fields: integrationFields('smartbill'),
      status: 'connected',
      source: 'db',
      hasStoredCredentials: true,
      isConnected: true,
      lastTestedAt: isoDateTime(-1, 9),
      lastTestResult: 'OK — sincronizare reușită',
      values: { username: 'demo@firmio.ro', token: '••••••••', companyVatCode: 'RO47847495' },
    },
    {
      key: 'googleCalendar',
      label: 'Google Calendar',
      description: 'Sincronizare calendar și evenimente echipă.',
      scope: 'user',
      fields: integrationFields('googleCalendar'),
      status: 'not_configured',
      source: 'none',
      hasStoredCredentials: false,
      isConnected: false,
      lastTestedAt: null,
      lastTestResult: null,
      values: {},
    },
    {
      key: 'anthropic',
      label: 'Anthropic (Claude AI)',
      description: 'Asistent AI pentru analiză și sumarizare.',
      scope: 'global',
      fields: integrationFields('anthropic'),
      status: 'connected',
      source: 'db',
      hasStoredCredentials: true,
      isConnected: true,
      lastTestedAt: isoDateTime(0, 8),
      lastTestResult: 'OK — model disponibil',
      values: { apiKey: '••••••••' },
    },
    {
      key: 'smtp',
      label: 'SMTP Email',
      description: 'Trimitere emailuri de notificare și rapoarte.',
      scope: 'global',
      fields: integrationFields('smtp'),
      status: 'connected',
      source: 'db',
      hasStoredCredentials: true,
      isConnected: true,
      lastTestedAt: isoDateTime(-1, 14),
      lastTestResult: 'OK — email de test trimis',
      values: {
        host: 'smtp.example.com',
        port: '587',
        user: 'noreply@firmio.ro',
        pass: '••••••••',
        from: 'noreply@firmio.ro',
      },
    },
    {
      key: 'googleDrive',
      label: 'Google Drive',
      description: 'Stocare și sincronizare documente în cloud.',
      scope: 'global',
      fields: integrationFields('googleDrive'),
      status: 'not_configured',
      source: 'none',
      hasStoredCredentials: false,
      isConnected: false,
      lastTestedAt: null,
      lastTestResult: null,
      values: {},
    },
    {
      key: 'anaf',
      label: 'ANAF e-Factura',
      description: 'Integrare cu sistemul e-Factura ANAF.',
      scope: 'company',
      fields: integrationFields('anaf'),
      status: 'not_configured',
      source: 'none',
      hasStoredCredentials: false,
      isConnected: false,
      lastTestedAt: null,
      lastTestResult: null,
      values: {},
    },
  ];
}

export function buildSettingsIntegration(key: string): SettingsIntegration | undefined {
  return buildSettingsIntegrations().find((i) => i.key === key);
}

export function buildBankDefaults(): BankDefaultsResponse {
  return {
    defaults: {
      creditCategoryCode: 'venituri',
      debitCategoryCode: 'cheltuieli',
    },
    categories: [
      { id: 'bdc-1', code: 'venituri', name: 'Venituri din servicii' },
      { id: 'bdc-2', code: 'cheltuieli', name: 'Cheltuieli operaționale' },
      { id: 'bdc-3', code: 'salarii', name: 'Salarii și contribuții' },
      { id: 'bdc-4', code: 'chirii', name: 'Chirii și utilități' },
      { id: 'bdc-5', code: 'software', name: 'Software și licențe' },
      { id: 'bdc-6', code: 'taxe', name: 'Taxe și impozite' },
      { id: 'bdc-7', code: 'transferuri', name: 'Transferuri interne' },
      { id: 'bdc-8', code: 'altele', name: 'Altele' },
    ],
  };
}

import type { Service as ApiService } from '../lib/servicesApi';
export type Service = ApiService;

export function buildServices(): Service[] {
  const now = isoDateTime(0, 10);
  const created = isoDateTime(-300, 10);
  return [
    {
      id: 'svc-1',
      name: 'Dezvoltare software',
      description: 'Programare și implementare funcționalități',
      price: 80,
      unit: 'HOUR',
      category: 'DEVELOPMENT',
      isActive: true,
      departmentId: 'dept-engineering',
      department: { id: 'dept-engineering', name: 'Engineering', color: '#3b82f6' },
      color: '#3b82f6',
      sortOrder: 0,
      taskCount: 12,
      projectCount: 5,
      personCount: 4,
      invoiceCount: 8,
      createdAt: created,
      updatedAt: now,
    },
    {
      id: 'svc-2',
      name: 'Design UI/UX',
      description: 'Design interfață și experiență utilizator',
      price: 70,
      unit: 'HOUR',
      category: 'DESIGN',
      isActive: true,
      departmentId: 'dept-design',
      department: { id: 'dept-design', name: 'Design', color: '#f97316' },
      color: '#f97316',
      sortOrder: 1,
      taskCount: 6,
      projectCount: 3,
      personCount: 2,
      invoiceCount: 4,
      createdAt: created,
      updatedAt: now,
    },
    {
      id: 'svc-3',
      name: 'Consultanță tehnică',
      description: 'Consultanță arhitectură și strategie tehnică',
      price: 100,
      unit: 'HOUR',
      category: 'CONSULTING',
      isActive: true,
      departmentId: null,
      department: null,
      color: '#8b5cf6',
      sortOrder: 2,
      taskCount: 3,
      projectCount: 2,
      personCount: 2,
      invoiceCount: 3,
      createdAt: created,
      updatedAt: now,
    },
    {
      id: 'svc-4',
      name: 'QA & Testing',
      description: 'Testare manuală și automatizată',
      price: 55,
      unit: 'HOUR',
      category: 'QA',
      isActive: true,
      departmentId: 'dept-quality',
      department: { id: 'dept-quality', name: 'Quality', color: '#eab308' },
      color: '#eab308',
      sortOrder: 3,
      taskCount: 8,
      projectCount: 4,
      personCount: 2,
      invoiceCount: 2,
      createdAt: created,
      updatedAt: now,
    },
    {
      id: 'svc-5',
      name: 'DevOps',
      description: 'Infrastructură, CI/CD și deployment',
      price: 85,
      unit: 'HOUR',
      category: 'DEVELOPMENT',
      isActive: true,
      departmentId: 'dept-infrastructure',
      department: { id: 'dept-infrastructure', name: 'Infrastructure', color: '#64748b' },
      color: '#64748b',
      sortOrder: 4,
      taskCount: 5,
      projectCount: 3,
      personCount: 1,
      invoiceCount: 2,
      createdAt: created,
      updatedAt: now,
    },
    {
      id: 'svc-6',
      name: 'Marketing digital',
      description: 'Campanii online și strategie de marketing',
      price: 60,
      unit: 'HOUR',
      category: 'OTHER',
      isActive: true,
      departmentId: 'dept-marketing',
      department: { id: 'dept-marketing', name: 'Marketing', color: '#ef4444' },
      color: '#ef4444',
      sortOrder: 5,
      taskCount: 4,
      projectCount: 2,
      personCount: 1,
      invoiceCount: 1,
      createdAt: created,
      updatedAt: now,
    },
    {
      id: 'svc-7',
      name: 'Training',
      description: 'Cursuri și workshop-uri tehnice',
      price: 90,
      unit: 'HOUR',
      category: 'OTHER',
      isActive: true,
      departmentId: null,
      department: null,
      color: '#22c55e',
      sortOrder: 6,
      taskCount: 1,
      projectCount: 1,
      personCount: 2,
      invoiceCount: 0,
      createdAt: created,
      updatedAt: now,
    },
    {
      id: 'svc-8',
      name: 'Suport tehnic',
      description: 'Mentenanță și suport aplicații',
      price: 50,
      unit: 'MONTH',
      category: 'MAINTENANCE',
      isActive: true,
      departmentId: null,
      department: null,
      color: '#0891b2',
      sortOrder: 7,
      taskCount: 2,
      projectCount: 2,
      personCount: 1,
      invoiceCount: 3,
      createdAt: created,
      updatedAt: now,
    },
    {
      id: 'svc-9',
      name: 'Project Management',
      description: 'Coordonare și livrare proiecte',
      price: 75,
      unit: 'HOUR',
      category: 'PM',
      isActive: true,
      departmentId: 'dept-delivery',
      department: { id: 'dept-delivery', name: 'Delivery', color: '#22c55e' },
      color: '#a855f7',
      sortOrder: 8,
      taskCount: 3,
      projectCount: 4,
      personCount: 2,
      invoiceCount: 2,
      createdAt: created,
      updatedAt: now,
    },
    {
      id: 'svc-10',
      name: 'Analiză de business',
      description: 'Analiză cerințe și documentare',
      price: 65,
      unit: 'HOUR',
      category: 'PM',
      isActive: true,
      departmentId: null,
      department: null,
      color: '#d97706',
      sortOrder: 9,
      taskCount: 2,
      projectCount: 2,
      personCount: 1,
      invoiceCount: 1,
      createdAt: created,
      updatedAt: now,
    },
  ];
}

import type { ExpenseCategory as ApiExpenseCategory } from '../lib/expenseCategoriesApi';
export type ExpenseCategory = ApiExpenseCategory;

export function buildExpenseCategories(): ExpenseCategory[] {
  return [
    {
      id: 'ecat-1',
      code: 'salarii',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Salarii și bonusuri',
      description: 'Salarii brute, bonusuri, contribuții',
      color: '#ef4444',
      isSystem: true,
      isActive: true,
      sortOrder: 0,
    },
    {
      id: 'ecat-2',
      code: 'chirii',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Chirii și utilități',
      description: 'Chirie birou, utilități, internet',
      color: '#f97316',
      isSystem: true,
      isActive: true,
      sortOrder: 1,
    },
    {
      id: 'ecat-3',
      code: 'subscriptii',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Subscripții SaaS',
      description: 'Software, licențe, subscripții cloud',
      color: '#3b82f6',
      isSystem: false,
      isActive: true,
      sortOrder: 2,
    },
    {
      id: 'ecat-4',
      code: 'contabilitate',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Contabilitate',
      description: 'Servicii contabilitate și audit',
      color: '#6b7280',
      isSystem: false,
      isActive: true,
      sortOrder: 3,
    },
    {
      id: 'ecat-5',
      code: 'marketing',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Marketing',
      description: 'Campanii, branding, events',
      color: '#ec4899',
      isSystem: false,
      isActive: true,
      sortOrder: 4,
    },
    {
      id: 'ecat-6',
      code: 'deplasari',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Deplasări',
      description: 'Transport, cazare, diurnă',
      color: '#8b5cf6',
      isSystem: false,
      isActive: true,
      sortOrder: 5,
    },
    {
      id: 'ecat-7',
      code: 'echipamente',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Echipamente IT',
      description: 'Laptopuri, monitoare, periferice',
      color: '#0891b2',
      isSystem: false,
      isActive: true,
      sortOrder: 6,
    },
    {
      id: 'ecat-8',
      code: 'telecom',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Telecomunicații',
      description: 'Telefonie, internet mobil',
      color: '#22c55e',
      isSystem: false,
      isActive: true,
      sortOrder: 7,
    },
    {
      id: 'ecat-9',
      code: 'asigurari',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Asigurări',
      description: 'Asigurări profesionale și de sănătate',
      color: '#eab308',
      isSystem: false,
      isActive: true,
      sortOrder: 8,
    },
    {
      id: 'ecat-10',
      code: 'juridic',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Consultanță juridică',
      description: null,
      color: '#64748b',
      isSystem: false,
      isActive: true,
      sortOrder: 9,
    },
    {
      id: 'ecat-11',
      code: 'training',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Training și dezvoltare',
      description: null,
      color: '#16a34a',
      isSystem: false,
      isActive: true,
      sortOrder: 10,
    },
    {
      id: 'ecat-12',
      code: 'altele',
      kind: 'EXPENSE',
      companyId: null,
      name: 'Altele',
      description: 'Cheltuieli diverse neclasificate',
      color: '#94a3b8',
      isSystem: true,
      isActive: true,
      sortOrder: 11,
    },
  ];
}

import type { BankAccount as ApiBankAccount } from '../lib/bankAccountsApi';
export type BankAccount = ApiBankAccount;

export function buildBankAccounts(): BankAccount[] {
  const created = isoDateTime(-180, 10);
  const updated = isoDateTime(-2, 14);
  return [
    {
      id: 'ba-1',
      iban: 'RO49BTRL0123456789012345',
      bankName: 'Banca Transilvania',
      label: 'Cont principal RON',
      currency: 'RON',
      isActive: true,
      companyId: null,
      company: null,
      manualBalance: null,
      manualBalanceAt: null,
      statementsCount: 12,
      createdAt: created,
      updatedAt: updated,
    },
    {
      id: 'ba-2',
      iban: 'RO49BTRL0123456789012346',
      bankName: 'Banca Transilvania',
      label: 'Cont EUR',
      currency: 'EUR',
      isActive: true,
      companyId: null,
      company: null,
      manualBalance: 12_400,
      manualBalanceAt: isoDateTime(-8, 12),
      statementsCount: 6,
      createdAt: created,
      updatedAt: updated,
    },
    {
      id: 'ba-3',
      iban: 'RO49INGB0001000100010001',
      bankName: 'ING Bank',
      label: 'Cont ING RON',
      currency: 'RON',
      isActive: true,
      companyId: 'co-2',
      company: { id: 'co-2', name: 'Firmio Studio SRL' },
      manualBalance: null,
      manualBalanceAt: null,
      statementsCount: 8,
      createdAt: created,
      updatedAt: updated,
    },
  ];
}

import type { CompanyProfile } from '../lib/companyProfileApi';

export function buildCompanyProfile(): CompanyProfile {
  return {
    id: 'co-1',
    legalName: 'Firmio Demo SRL',
    displayName: 'Firmio Demo',
    cui: 'RO47847495',
    regCom: 'J12/100/2019',
    address: 'Str. Exemplu nr. 1',
    city: 'Cluj-Napoca',
    county: 'Cluj',
    country: 'România',
    iban: 'RO49AAAA1B31007593840001',
    bank: 'Banca Transilvania',
    email: 'office@firmio-demo.ro',
    phone: '+40 264 000 001',
    website: 'https://firmio-demo.ro',
    logoUrl: null,
    defaultCurrency: 'RON',
    vatPayer: true,
    createdAt: isoDateTime(-540, 9),
    updatedAt: isoDateTime(-5, 14),
  };
}

export function buildOnboardingStatus() {
  const people = buildPeople();
  const tasks = buildTasks();
  const invoices = buildInvoices();
  const projects = PROJECT_SEEDS;
  const clients = CLIENT_SEEDS;
  const suppliers = SUPPLIER_SEEDS;
  const subscriptions = buildSubscriptions();

  return {
    steps: [
      { key: 'company', label: 'Profil firmă', completed: true, count: 1 },
      { key: 'team', label: 'Echipă', completed: true, count: people.length },
      { key: 'clients', label: 'Clienți', completed: true, count: clients.length },
      { key: 'suppliers', label: 'Furnizori', completed: true, count: suppliers.length },
      { key: 'projects', label: 'Proiecte', completed: true, count: projects.length },
      { key: 'tasks', label: 'Sarcini', completed: true, count: tasks.length },
      { key: 'invoices', label: 'Facturi', completed: true, count: invoices.length },
      { key: 'subscriptions', label: 'Abonamente', completed: true, count: subscriptions.length },
      { key: 'bank', label: 'Extrase bancare', completed: true, count: 3 },
      { key: 'integrations', label: 'Integrări', completed: false, count: 2 },
    ],
    completionPercent: 90,
  };
}

export function buildOnboardingIntegrations() {
  return {
    smartbill: { configured: true, connected: true, message: 'Conectat (Test Mode)' },
    googleCalendar: { configured: false, connected: false, message: null },
    anthropic: { configured: true, connected: true, message: 'API key configurat' },
    smtp: { configured: true, connected: true, message: 'SMTP configurat' },
    googleDrive: { configured: false, connected: false, message: null },
  };
}

export function buildAiUsage(period?: AiUsagePeriod): AiUsageSummary {
  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonth = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  return {
    month,
    costEur: 18.5,
    budgetEur: 50,
    callCount: 245,
    period: period ?? { kind: 'month', month },
    periodCostEur: 18.5,
    periodCallCount: 245,
    availableMonths: [month, prevMonth],
    dailySince: `${month}-01`,
    perModel: [
      {
        provider: 'anthropic',
        model: 'claude-sonnet-4-20250514',
        inputTokens: '845000',
        outputTokens: '212000',
        callCount: 180,
        costEur: 12.4,
      },
      {
        provider: 'anthropic',
        model: 'claude-haiku-4-20250514',
        inputTokens: '400000',
        outputTokens: '100000',
        callCount: 65,
        costEur: 6.1,
      },
    ],
  };
}

// --- Financial analytics (Analiză tab) ------------------------------------

import type { AnalyticsDimension } from '../lib/financialsApi';

const ANALYTICS_COLORS = [
  '#4f46e5',
  '#0891b2',
  '#059669',
  '#d97706',
  '#dc2626',
  '#7c3aed',
  '#be185d',
  '#0284c7',
  '#65a30d',
  '#ea580c',
];

export function buildFinancialAnalytics(dimension: string) {
  const dim = dimension as AnalyticsDimension;
  const now = new Date();

  type RowDef = { key: string; label: string; income: number; expenses: number };

  const rowDefs: Record<string, RowDef[]> = {
    service: [
      { key: 'svc-1', label: 'Dezvoltare software', income: 182_000, expenses: 98_000 },
      { key: 'svc-2', label: 'Design UI/UX', income: 48_000, expenses: 24_000 },
      { key: 'svc-3', label: 'Consultanță tehnică', income: 67_000, expenses: 18_000 },
      { key: 'svc-4', label: 'QA & Testing', income: 32_000, expenses: 16_000 },
      { key: 'svc-5', label: 'DevOps', income: 28_000, expenses: 12_000 },
      { key: 'svc-9', label: 'Project Management', income: 22_000, expenses: 14_000 },
    ],
    client: CLIENT_SEEDS.map((c) => ({
      key: c.id,
      label: c.name,
      income: 35_000 + (c.id.charCodeAt(1) % 5) * 18_000,
      expenses: 0,
    })),
    supplier: SUPPLIER_SEEDS.map((s) => ({
      key: s.id,
      label: s.name,
      income: 0,
      expenses: s.monthlyEur * 5 * 6,
    })),
    firm: [
      { key: 'co-1', label: 'Firmio Demo SRL', income: 320_000, expenses: 195_000 },
      { key: 'co-2', label: 'Firmio Studio SRL', income: 59_000, expenses: 32_000 },
    ],
    department: [
      { key: 'dep-eng', label: 'Engineering', income: 210_000, expenses: 120_000 },
      { key: 'dep-del', label: 'Delivery', income: 85_000, expenses: 42_000 },
      { key: 'dep-des', label: 'Design', income: 48_000, expenses: 24_000 },
      { key: 'dep-mkt', label: 'Marketing', income: 18_000, expenses: 28_000 },
      { key: 'dep-ops', label: 'Operations', income: 0, expenses: 14_000 },
    ],
    subscription: buildSubscriptions()
      .slice(0, 8)
      .map((s) => ({
        key: s.id,
        label: s.name,
        income: 0,
        expenses: (s.expectedAmount ?? 100) * 6,
      })),
    person: buildPeople()
      .slice(0, 10)
      .map((p) => ({
        key: p.id,
        label: p.fullName,
        income: 18_000 + (p.id.charCodeAt(1) % 7) * 8_000,
        expenses: 6_000 + (p.id.charCodeAt(1) % 4) * 3_000,
      })),
  };

  const rows = (rowDefs[dim] ?? rowDefs.service).map((r, i) => ({
    key: r.key,
    label: r.label,
    color: ANALYTICS_COLORS[i % ANALYTICS_COLORS.length],
    income: r.income,
    expenses: r.expenses,
    profit: r.income - r.expenses,
  }));

  const totals = rows.reduce(
    (acc, r) => ({
      income: acc.income + r.income,
      expenses: acc.expenses + r.expenses,
      profit: acc.profit + r.profit,
    }),
    { income: 0, expenses: 0, profit: 0 },
  );

  return {
    dimension: dim,
    period: { year: now.getFullYear(), month: null },
    rows,
    totals,
    notes: [],
  };
}
